import { lazy, Suspense, Component } from 'react';
import type { ReactNode } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { AuthGuard } from '@/guards/AuthGuard';
import { PublicGuard } from '@/guards/PublicGuard';
import { RoleGuard } from '@/guards/RoleGuard';
import { ROLES } from '@/constants';
import { AuthLayout } from '@/layouts/AuthLayout/AuthLayout';
import { DashboardLayout } from '@/layouts/DashboardLayout/DashboardLayout';
import { LoadingScreen } from '@/components/common/loaders/LoadingScreen';

class RouteErrorBoundary extends Component<
  { children: ReactNode },
  { hasError: boolean; error: Error | null }
> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="flex flex-col items-center justify-center h-96 text-center p-8">
          <div className="text-4xl mb-4">⚠️</div>
          <h2 className="text-lg font-semibold text-gray-700 mb-2">Something went wrong</h2>
          <p className="text-gray-400 text-sm mb-4">
            {this.state.error?.message ?? 'An unexpected error occurred'}
          </p>
          <button
            onClick={() => {
              this.setState({ hasError: false, error: null });
              window.location.href = '/my-space';
            }}
            className="px-4 py-2 text-sm bg-[#219268] text-white rounded-lg hover:bg-[#1a7a56]"
          >
            Go to Dashboard
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

// ── Lazy-loaded pages ─────────────────────────────────────────────────────
const LoginPage        = lazy(() => import('@/pages/auth/LoginPage'));
const UnauthorizedPage = lazy(() => import('@/pages/auth/UnauthorizedPage'));
const NotFoundPage     = lazy(() => import('@/pages/common/NotFoundPage'));

const MySpacePage      = lazy(() => import('@/pages/dashboard/MySpacePage'));
const ProfilePage      = lazy(() => import('@/pages/employee/ProfilePage'));
const LeavePage        = lazy(() => import('@/pages/leave/LeavePage'));
const AttendancePage   = lazy(() => import('@/pages/attendance/AttendancePage'));
const HolidaysPage     = lazy(() => import('@/pages/leave/HolidaysPage'));
const PayrollPage      = lazy(() => import('@/pages/payroll/PayrollPage'));
const PerformancePage  = lazy(() => import('@/pages/performance/PerformancePage'));
const RequestsPage     = lazy(() => import('@/pages/requests/RequestsPage'));
const HelpdeskPage     = lazy(() => import('@/pages/helpdesk/HelpdeskPage'));
const ReqCentrePage    = lazy(() => import('@/pages/requests/ReqCentrePage'));
const HierarchyPage    = lazy(() => import('@/pages/employee/HierarchyPage'));

const LeaveLedgerPage  = lazy(() => import('@/pages/ops/LeaveLedgerPage'));
const LettersPage      = lazy(() => import('@/pages/ops/LettersPage'));
const AssetManagementPage = lazy(() => import('@/pages/ops/AssetManagementPage'));

const AdminDashboard   = lazy(() => import('@/pages/admin/AdminDashboardPage'));
const OpsEmployeesPage = lazy(() => import('@/pages/admin/OpsEmployeesPage'));

const ConfigOrgPage    = lazy(() => import('@/pages/config/ConfigOrgPage'));
const OrganizationsPage = lazy(() => import('@/pages/config/OrganizationsPage'));
const ConfigHolidayCalendarPage = lazy(() => import('@/pages/config/ConfigHolidayCalendarPage'));
const ConfigLeavePage  = lazy(() => import('@/pages/config/ConfigLeavePage'));
const EmployeeDataPage = lazy(() => import('@/pages/config/EmployeeDataPage'));
const ConfigAssetsPage = lazy(() => import('@/pages/config/ConfigAssetsPage'));
const ConfigHelpdeskPage = lazy(() => import('@/pages/config/ConfigHelpdeskPage'));
const ConfigLettersPage = lazy(() => import('@/pages/config/ConfigLettersPage'));
const ConfigNoticeBoardPage = lazy(() => import('@/pages/config/ConfigNoticeBoardPage'));

const Lazy = ({ children }: { children: React.ReactNode }) => (
  <Suspense fallback={<LoadingScreen />}>{children}</Suspense>
);

export function AppRoutes() {
  return (
    <Routes>
      {/* ── Public Routes ───────────────────────────────────────── */}
      <Route element={<PublicGuard><AuthLayout /></PublicGuard>}>
        <Route path="/login" element={<Lazy><LoginPage /></Lazy>} />
      </Route>

      <Route path="/unauthorized" element={<Lazy><UnauthorizedPage /></Lazy>} />

      {/* ── Protected Routes ─────────────────────────────────────── */}
      <Route element={<AuthGuard><DashboardLayout /></AuthGuard>}>
        {/* Employee Module */}
        <Route path="/my-space"    element={<Lazy><MySpacePage /></Lazy>} />
        <Route path="/profile/:employeeId?" element={<Lazy><ProfilePage /></Lazy>} />
        <Route path="/leave"       element={<Lazy><LeavePage /></Lazy>} />
        <Route path="/attendance"  element={<Lazy><AttendancePage /></Lazy>} />
        <Route path="/holidays"    element={<Lazy><HolidaysPage /></Lazy>} />
        <Route path="/payroll"     element={<Lazy><PayrollPage /></Lazy>} />
        <Route path="/performance" element={<Lazy><PerformancePage /></Lazy>} />
        <Route path="/requests"    element={<Lazy><RequestsPage /></Lazy>} />
        <Route path="/helpdesk"    element={<Lazy><HelpdeskPage /></Lazy>} />
        <Route path="/req-centre"  element={
          <RouteErrorBoundary><Lazy><ReqCentrePage /></Lazy></RouteErrorBoundary>
        } />
        <Route path="/hierarchy"   element={<Lazy><HierarchyPage /></Lazy>} />

        {/* Admin Operations */}
        <Route path="/admin/dashboard" element={
          <RoleGuard allowedRoles={[ROLES.ADMIN]}><Lazy><AdminDashboard /></Lazy></RoleGuard>
        } />
        <Route path="/ops/employees" element={
          <RoleGuard allowedRoles={[ROLES.ADMIN, ROLES.HR]}><Lazy><OpsEmployeesPage /></Lazy></RoleGuard>
        } />
        <Route path="/ops/leave-ledger" element={
          <RoleGuard allowedRoles={[ROLES.ADMIN, ROLES.HR]}><Lazy><LeaveLedgerPage /></Lazy></RoleGuard>
        } />
        <Route path="/ops/letters" element={
          <RoleGuard allowedRoles={[ROLES.ADMIN, ROLES.HR]}><Lazy><LettersPage /></Lazy></RoleGuard>
        } />
        <Route path="/ops/assets" element={
          <RoleGuard allowedRoles={[ROLES.ADMIN, ROLES.HR]}><Lazy><AssetManagementPage /></Lazy></RoleGuard>
        } />

        {/* Config Module - Admin / HR */}
        <Route path="/config/organizations" element={
          <RoleGuard allowedRoles={[ROLES.ADMIN]}><Lazy><OrganizationsPage /></Lazy></RoleGuard>
        } />
        <Route path="/config/org" element={
          <RoleGuard allowedRoles={[ROLES.ADMIN]}><Lazy><ConfigOrgPage /></Lazy></RoleGuard>
        } />
        <Route path="/config/holiday-calendar" element={
          <RoleGuard allowedRoles={[ROLES.ADMIN]}><Lazy><ConfigHolidayCalendarPage /></Lazy></RoleGuard>
        } />
        <Route path="/config/leave" element={
          <RoleGuard allowedRoles={[ROLES.ADMIN]}><Lazy><ConfigLeavePage /></Lazy></RoleGuard>
        } />
        <Route path="/config/empdata" element={
          <RoleGuard allowedRoles={[ROLES.ADMIN, ROLES.HR]}><Lazy><EmployeeDataPage /></Lazy></RoleGuard>
        } />
        <Route path="/config/assets" element={
          <RoleGuard allowedRoles={[ROLES.ADMIN]}><Lazy><ConfigAssetsPage /></Lazy></RoleGuard>
        } />
        <Route path="/config/helpdesk" element={
          <RoleGuard allowedRoles={[ROLES.ADMIN]}><Lazy><ConfigHelpdeskPage /></Lazy></RoleGuard>
        } />
        <Route path="/config/letters" element={
          <RoleGuard allowedRoles={[ROLES.ADMIN]}><Lazy><ConfigLettersPage /></Lazy></RoleGuard>
        } />
        <Route path="/config/notices" element={
          <RoleGuard allowedRoles={[ROLES.ADMIN, ROLES.HR]}><Lazy><ConfigNoticeBoardPage /></Lazy></RoleGuard>
        } />

        {/* Catch-all */}
        <Route path="/" element={<Navigate to="/my-space" replace />} />
      </Route>

      {/* ── 404 ──────────────────────────────────────────────────── */}
      <Route path="*" element={<Lazy><NotFoundPage /></Lazy>} />
    </Routes>
  );
}
